/**
 * patient360PriorityEngine.ts — Deterministic "AI-style" priority scoring
 * for Patient360 smart sections.
 *
 * No external AI calls — uses rule-based scoring from patient state signals.
 * Mirrors the backend Patient360SectionPriorityService logic for client-side
 * re-scoring when real-time signals change (e.g. alert dismiss).
 */

import type {
  Patient360ResolvedSection,
  DynamicAlertViewModel,
  DynamicLifecycleViewModel,
  DynamicKpiCardViewModel,
} from '../types/dynamicEntityView';

// ─── Signal context ──────────────────────────────────────────

export interface PrioritySignals {
  alerts: DynamicAlertViewModel[];
  lifecycle: DynamicLifecycleViewModel;
  kpis: DynamicKpiCardViewModel[];
  hasActiveCase: boolean;
  hasActiveCycle: boolean;
  hasLowBalance: boolean;
  hasRenewalDue: boolean;
  hasExpiringCycle: boolean;
  hasMissingDocs: boolean;
  hasRecentActivity: boolean;
}

export interface PriorityResult {
  priorityScore: number;
  recommendedDefaultExpanded: boolean;
  badges: string[];
}

// ─── Badge constants ─────────────────────────────────────────

const BADGE = {
  URGENT: 'Urgent',
  RENEWAL_DUE: 'Renewal Due',
  LOW_BALANCE: 'Low Balance',
  MISSING_DOCS: 'Missing Docs',
  RECENT_ACTIVITY: 'Recent Activity',
  WORKFLOW_REQUIRED: 'Workflow Required',
  ACTIVE_CYCLE: 'Active Cycle',
  EXPIRING_SOON: 'Expiring Soon',
  NO_ACTIVE_CASE: 'No Active Enrollment',
} as const;

// ─── Score computation ───────────────────────────────────────

export function computeSectionPriority(
  section: Patient360ResolvedSection,
  signals: PrioritySignals,
): PriorityResult {
  const badges: string[] = [];
  let score = section.priorityScore; // start with backend-computed score

  // If backend didn't compute a score, use a base from configuredOrder
  if (score === 0) {
    score = Math.max(0, 100 - section.configuredOrder * 10);
  }

  // ── Risk signals ─────────────────────────────────────────
  const hasCritical = signals.alerts.some(a => a.severity === 'critical');
  const hasWarning = signals.alerts.some(a => a.severity === 'warning');

  if (hasCritical) score += 20;
  if (hasWarning) score += 10;

  // ── Section-specific boosts ──────────────────────────────
  switch (section.sectionKey) {
    case 'cycle_details':
      if (signals.hasActiveCycle) {
        score += 15;
        addBadge(badges, BADGE.ACTIVE_CYCLE);
      }
      if (signals.hasLowBalance) {
        score += 25;
        addBadge(badges, BADGE.LOW_BALANCE);
      }
      if (signals.hasExpiringCycle) {
        score += 20;
        addBadge(badges, BADGE.EXPIRING_SOON);
      }
      break;

    case 'program_enrollment':
      if (signals.hasRenewalDue) {
        score += 20;
        addBadge(badges, BADGE.RENEWAL_DUE);
      }
      if (!signals.hasActiveCase) {
        score += 10;
        addBadge(badges, BADGE.NO_ACTIVE_CASE);
      }
      break;

    case 'recent_activity':
      if (signals.hasRecentActivity) {
        score += 15;
        addBadge(badges, BADGE.RECENT_ACTIVITY);
      }
      break;

    case 'additional_patient_info':
      if (signals.hasMissingDocs) {
        score += 15;
        addBadge(badges, BADGE.MISSING_DOCS);
      }
      break;
  }

  // ── Data completeness ──────────────────────────────────
  if (!section.isEmpty) {
    score += 5;
  } else {
    score -= 20;
  }

  // ── Urgency badge ──────────────────────────────────────
  if (hasCritical && (section.sectionKey === 'cycle_details' || section.sectionKey === 'program_enrollment')) {
    addBadge(badges, BADGE.URGENT);
  }

  // Merge any server-side badges that aren't already present
  for (const b of section.badges) {
    addBadge(badges, b);
  }

  // ── Determine recommended expansion ────────────────────
  let recommendedDefaultExpanded = section.defaultExpanded;
  if (score >= 90) recommendedDefaultExpanded = true;
  if (section.isEmpty && score < 50) recommendedDefaultExpanded = false;

  return {
    priorityScore: Math.max(0, score),
    recommendedDefaultExpanded,
    badges,
  };
}

// ─── Sort sections by priority ───────────────────────────────

export function sortSectionsByPriority(
  sections: Patient360ResolvedSection[],
  signals: PrioritySignals,
): (Patient360ResolvedSection & PriorityResult)[] {
  return sections
    .map(section => {
      const result = computeSectionPriority(section, signals);
      return { ...section, ...result };
    })
    .sort((a, b) => b.priorityScore - a.priorityScore);
}

// ─── Extract signals from view model data ────────────────────

export function extractPrioritySignals(opts: {
  alerts: DynamicAlertViewModel[];
  lifecycle: DynamicLifecycleViewModel;
  kpis: DynamicKpiCardViewModel[];
  hasActiveCase: boolean;
  activeCycleNumber?: number;
  remainingCap?: number | null;
  cycleEnd?: string | null;
  caseExpiry?: string | null;
  lastActivityAt?: string | null;
}): PrioritySignals {
  const now = Date.now();

  const hasActiveCycle = (opts.activeCycleNumber ?? 0) > 0;

  // Low balance from KPIs or direct value
  let hasLowBalance = false;
  const remainingKpi = opts.kpis.find(
    k => k.kpiKey === 'remaining_cap' || k.kpiKey === 'remaining_benefit',
  );
  if (remainingKpi?.numericValue != null && remainingKpi.numericValue < 500)
    hasLowBalance = true;
  if (opts.remainingCap != null && opts.remainingCap < 500)
    hasLowBalance = true;
  if (opts.alerts.some(a =>
    a.message.toLowerCase().includes('low balance') ||
    a.message.toLowerCase().includes('low remaining')))
    hasLowBalance = true;

  // Expiry/renewal
  let hasRenewalDue = false;
  let hasExpiringCycle = false;
  if (opts.caseExpiry) {
    const expiry = new Date(opts.caseExpiry).getTime();
    if (expiry - now < 30 * 24 * 60 * 60 * 1000) hasRenewalDue = true;
  }
  if (opts.cycleEnd) {
    const end = new Date(opts.cycleEnd).getTime();
    if (end - now < 7 * 24 * 60 * 60 * 1000) hasExpiringCycle = true;
  }
  if (opts.alerts.some(a =>
    a.message.toLowerCase().includes('renewal') ||
    a.message.toLowerCase().includes('expir')))
    hasRenewalDue = true;

  // Missing docs
  const hasMissingDocs = opts.alerts.some(a =>
    a.message.toLowerCase().includes('missing') &&
    a.message.toLowerCase().includes('document'));

  // Recent activity
  let hasRecentActivity = false;
  if (opts.lastActivityAt) {
    const lastActivity = new Date(opts.lastActivityAt).getTime();
    if (now - lastActivity < 3 * 24 * 60 * 60 * 1000) hasRecentActivity = true;
  }

  return {
    alerts: opts.alerts,
    lifecycle: opts.lifecycle,
    kpis: opts.kpis,
    hasActiveCase: opts.hasActiveCase,
    hasActiveCycle,
    hasLowBalance,
    hasRenewalDue,
    hasExpiringCycle,
    hasMissingDocs,
    hasRecentActivity,
  };
}

// ─── Filter visible sections ─────────────────────────────────

export function filterVisibleSections(
  sections: Patient360ResolvedSection[],
  opts?: {
    hideEmpty?: boolean;
    hiddenSectionKeys?: Set<string>;
  },
): Patient360ResolvedSection[] {
  const hideEmpty = opts?.hideEmpty ?? true;
  const hiddenKeys = opts?.hiddenSectionKeys;

  return sections.filter(s => {
    // Workflow/role hidden
    if (hiddenKeys?.has(s.sectionKey)) return false;
    // Empty section filtering
    if (hideEmpty && s.isEmpty && !s.defaultExpanded) return false;
    // Section with no visible fields
    if (s.fields.length === 0 && !s.badges.length) return false;
    return true;
  });
}

// ─── Helpers ─────────────────────────────────────────────────

function addBadge(badges: string[], badge: string) {
  if (!badges.includes(badge)) badges.push(badge);
}
